import { forwardRef, useState, useEffect } from 'react';
import type { PropsWithoutRef } from 'react';
import type { PegaCustom } from 'slate';
import styled, { css } from 'styled-components';

import {
  HTML,
  ErrorState,
  useI18n,
  useTestIds,
  withTestIds,
  hasProp,
  defaultThemeProp
} from '@pega/cosmos-react-core';
import type { ForwardRefForwardPropsComponent } from '@pega/cosmos-react-core';

import convertMarkdown from './utils/markdownConverter';
import type { RichTextViewerProps } from './RichTextViewer.types';
import { StyledEditorContainer, StyledRichTextViewer } from './RichTextEditor.styles';
import { convertHtml } from './utils/htmlConverter';
import { RichTextTreeRenderer } from './RichTextTreeRenderer';
import type { RichTextTreeRendererProps } from './RichTextTreeRenderer';
import { getRichTextViewerTestIds } from './RichTextViewer.test-ids';

const StyledHTMLViewer = styled(HTML)(({
  theme: {
    base: { spacing }
  }
}) => {
  return css`
    padding: calc(0.125 * ${spacing});
  `;
});

StyledHTMLViewer.defaultProps = defaultThemeProp;

// Regex to match valid URLs
export const URL_REGEX =
  /(?<leadingChar>^|\s)(?<url>(?<protocol>https?:\/\/)?(?<host>[a-z0-9]+(?:[-.][a-z0-9]+)*\.(?<tld>[a-z]{2,5})(?<port>:[0-9]{1,5})?)(?<pathQueryHash>[/a-zA-Z0-9?=&+%@.|#_~:[\]'()-]*)?)/g;

const isValidHTTPURL = (href?: string): boolean => {
  if (!href) return false;

  try {
    const url = new URL(href, window.location.href);
    return url.protocol.startsWith('http');
  } catch {
    return false;
  }
};

const findFirstHref = (nodes: (PegaCustom.Element | PegaCustom.Text)[]): string | undefined => {
  for (const node of nodes) {
    if (hasProp(node, 'children')) {
      const href = findFirstHref(node.children);
      if (isValidHTTPURL(href)) {
        return href;
      }
    } else if (isValidHTTPURL(node.href)) {
      return node.href;
    }
  }
};

const RichTextViewer: ForwardRefForwardPropsComponent<RichTextViewerProps> = forwardRef(
  function RichTextViewer(
    {
      testId,
      content,
      type,
      interactionRenderers,
      markdownMap,
      onRetry,
      onMarkdownLinkRender,
      renderHTML = false,
      ...restProps
    }: PropsWithoutRef<RichTextViewerProps>,
    ref: RichTextViewerProps['ref']
  ) {
    const testIds = useTestIds(testId, getRichTextViewerTestIds);
    const t = useI18n();
    const [nodes, setNodes] = useState<RichTextTreeRendererProps['nodes']>([]);
    const [error, setError] = useState(false);

    useEffect(() => {
      if (type === 'markdown') {
        const newNodes = convertMarkdown(
          content,
          [...(interactionRenderers ?? []), { regexPattern: URL_REGEX, type: 'builtin-url' }],
          markdownMap,
          renderHTML
        );

        if (onMarkdownLinkRender) {
          const href = findFirstHref(newNodes);
          if (href) {
            onMarkdownLinkRender(href);
          }
        }

        setNodes(newNodes);
      }
      if (type === 'richtext') {
        setNodes(JSON.parse(content));
      }
      if (type === 'html' && interactionRenderers && interactionRenderers.length > 0) {
        try {
          setNodes(convertHtml(content, interactionRenderers));
          setError(false);
        } catch {
          setNodes([]);
          setError(true);
        }
      }
    }, [content, type, interactionRenderers, renderHTML]);

    if (type === 'html' && (!interactionRenderers || interactionRenderers.length === 0)) {
      return <StyledHTMLViewer ref={ref} content={content} />;
    }

    return error ? (
      <ErrorState message={t('rte_invalid_html')} onRetry={onRetry} />
    ) : (
      <StyledRichTextViewer data-testid={testIds.root} ref={ref} {...restProps}>
        <StyledEditorContainer>
          <RichTextTreeRenderer nodes={nodes} />
        </StyledEditorContainer>
      </StyledRichTextViewer>
    );
  }
);

export default withTestIds(RichTextViewer, getRichTextViewerTestIds);
